import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  APP_PACKAGE_IDS,
  type AppKey,
  type AppPlatform,
  type AppVersionCheck,
} from '@capsico/contracts';
import { fetchAppVersion } from '../api/appVersion';

/*
 * W23 "please update" in the customer app: checks GET /v1/app/version on start and whenever the
 * app returns to the foreground (once the last answer is older than 30 minutes).
 * Fail-open: a 404 (feature off), an offline phone or any other error never blocks the app.
 */

const APP: AppKey = 'customer';
const STALE_MS = 30 * 60_000;
const LATER_KEY = 'capsico:customer:update-later';
/** The native shell appends `CapsicoApp/<app>/<versionCode>` to the WebView user agent. */
const NATIVE_UA = /CapsicoApp\/(customer|partner|rider|mi)\/(\d+)/;

export interface AppRuntime {
  platform: AppPlatform;
  /** The native build (Android versionCode); undefined on the web. */
  build: number | undefined;
}

/** Native shell → android (or ios) with its build; anything else → web without a build. */
export function detectRuntime(userAgent: string): AppRuntime {
  const match = NATIVE_UA.exec(userAgent);
  if (!match || match[1] !== APP) return { platform: 'web', build: undefined };
  const build = Number(match[2]);
  return {
    platform: /iPhone|iPad/.test(userAgent) ? 'ios' : 'android',
    build: Number.isSafeInteger(build) ? build : undefined,
  };
}

export function playStoreUrl(app: AppKey): string {
  return `https://play.google.com/store/apps/details?id=${APP_PACKAGE_IDS[app]}`;
}

export type UpdateAction = { kind: 'open'; url: string } | { kind: 'reload' };

/** Web builds update by reloading; native builds go to the store (Play Store when none is set). */
export function updateActionFor(check: AppVersionCheck, platform: AppPlatform): UpdateAction {
  if (platform === 'web') return { kind: 'reload' };
  if (check.storeUrl) return { kind: 'open', url: check.storeUrl };
  if (platform === 'android') return { kind: 'open', url: playStoreUrl(APP) };
  return { kind: 'reload' };
}

/** "Later" is remembered per latest build, so an optional update is offered once per build. */
function laterToken(check: AppVersionCheck): string {
  return String(check.latestBuild ?? check.latestVersion ?? '');
}

function readLater(): string | null {
  try {
    return window.localStorage.getItem(LATER_KEY);
  } catch {
    return null;
  }
}

function writeLater(token: string): void {
  try {
    window.localStorage.setItem(LATER_KEY, token);
  } catch {
    // Storage blocked: the sheet stays hidden for this session only.
  }
}

export type UpdateMode = 'none' | 'required' | 'available';

export function useAppUpdate() {
  const client = useQueryClient();
  const [runtime] = useState(() =>
    detectRuntime(typeof navigator === 'undefined' ? '' : navigator.userAgent),
  );
  const queryKey = useMemo(
    () => ['app-version', APP, runtime.platform, runtime.build ?? null] as const,
    [runtime],
  );
  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<AppVersionCheck | null> => {
      try {
        return await fetchAppVersion({
          app: APP,
          platform: runtime.platform,
          build: runtime.build,
        });
      } catch {
        // 404 (feature off), offline, server error: treat as "ok".
        return null;
      }
    },
    staleTime: STALE_MS,
    retry: false,
    // The foreground check below owns refetching.
    refetchOnWindowFocus: false,
  });

  // Back in the foreground: ask again once the last answer is stale.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void client.refetchQueries({ queryKey, stale: true });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [client, queryKey]);

  const [later, setLater] = useState<string | null>(readLater);
  const [hidden, setHidden] = useState(false);

  const check = query.data ?? null;
  const token = check ? laterToken(check) : '';
  let mode: UpdateMode = 'none';
  if (check?.verdict === 'update_required') mode = 'required';
  else if (check?.verdict === 'update_available' && !hidden && later !== token) {
    mode = 'available';
  }
  const action = useMemo<UpdateAction>(
    () => (check ? updateActionFor(check, runtime.platform) : { kind: 'reload' }),
    [check, runtime.platform],
  );

  const update = useCallback(() => {
    if (action.kind === 'reload') {
      window.location.reload();
      return;
    }
    window.open(action.url, '_blank', 'noopener');
    // An optional update steps aside once the store is open; a required one stays.
    if (mode === 'available') setHidden(true);
  }, [action, mode]);

  const dismiss = useCallback(() => {
    if (mode !== 'available') return;
    writeLater(token);
    setLater(token);
  }, [mode, token]);

  return {
    mode,
    actionKind: action.kind,
    latestVersion: check?.latestVersion ?? null,
    notes: check?.notes ?? null,
    update,
    later: dismiss,
  };
}
